import React, { useState } from "react";
import { withStyles } from "@material-ui/core/styles";
import { Mutation, Query } from "react-apollo";
import { gql } from "apollo-boost";
import { Button } from "@material-ui/core";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import MenuItem from "@material-ui/core/MenuItem";
import Grid from "@material-ui/core/Grid";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import Select from "react-select";
import { CountryRegionDataUSFirst } from "common/countries";
import { getAllDealerOptions } from "queries";
import { getBrands, getDealerOptions } from "./dealerOptions";
import "./BoatShowEdit.scss";

const styles = () => ({
  root: {
    textAlign: "left",
    padding: "8px 8px 0",
  },
  heading: {
    fontSize: "20px",
    fontWeight: "300",
    textTransform: "uppercase",
    textAlign: "center",
    paddingBottom: "0.5em",
  },
  fieldLabel: {
    display: "block",
    fontSize: "12px",
    color: "rgba(0, 0, 0, 0.54)",
    marginBottom: 6,
  },
  // Match the outlined site select's height so the two boxes line up
  dealerSelect: {
    maxWidth: "none !important",
    "& > div": { minHeight: 50 },
  },
  siteSelect: {
    "& .MuiSelect-select": { textAlign: "left" },
  },
  actions: {
    display: "flex",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: "16px",
  },
});

// All text inputs share the same look: full column width, label always on top
const fieldProps = {
  fullWidth: true,
  margin: "none",
  InputLabelProps: { shrink: true },
};

// Region names for a country name, e.g. "United States" -> ["Alabama", ...]
const getRegionNames = (countryName) => {
  const country = CountryRegionDataUSFirst.find((c) => c[0] === countryName);
  return country ? country[2].split("|").map((pair) => pair.split("~")[0]) : [];
};

const UPDATE_BOAT_SHOWS = gql`
  mutation updateBoatShows(
    $id: uuid!
    $dealer: String
    $profile_id: String
    $brands: String
    $name: String
    $start_date: date
    $end_date: date
    $address: String
    $address2: String
    $country: String
    $city: String
    $zipcode: String
    $region: String
    $description: String
    $site: String
    $modified_at: date
  ) {
    update_boat_shows(
      where: { id: { _eq: $id } }
      _set: {
        dealer: $dealer
        profile_id: $profile_id
        brands: $brands
        name: $name
        start_date: $start_date
        end_date: $end_date
        address: $address
        address2: $address2
        country: $country
        city: $city
        zipcode: $zipcode
        region: $region
        description: $description
        site: $site
        modified_at: $modified_at
      }
    ) {
      affected_rows
      returning {
        id
      }
    }
  }
`;

// Find the dealer currently linked to the show: by profile first, then by the
// saved dealer name (older shows were saved against the admin's profile).
const findCurrentDealer = (options, boatShow) =>
  options.find((option) => option.value === boatShow.profile_id) ||
  options.find(
    (option) =>
      option.label.toLowerCase() === (boatShow.dealer || "").toLowerCase()
  ) ||
  null;

const BoatShowsEditForm = ({ classes, boatShows, closeEditForm, dealerOptions }) => {
  const initialDealer = findCurrentDealer(dealerOptions, boatShows);
  // If the saved dealer no longer matches a profile, keep it as a selectable
  // option so the form still loads with the current value instead of blank.
  const options =
    !initialDealer && boatShows.dealer
      ? [{ label: boatShows.dealer, value: null, profile: null }, ...dealerOptions]
      : dealerOptions;

  const [dealer, setDealer] = useState(
    initialDealer || (boatShows.dealer ? options[0] : null)
  );
  const [form, setForm] = useState({
    name: boatShows.name || "",
    start_date: boatShows.start_date || "",
    end_date: boatShows.end_date || "",
    address: boatShows.address || "",
    address2: boatShows.address2 || "",
    country: boatShows.country || "",
    region: boatShows.region || "",
    zipcode: boatShows.zipcode || "",
    city: boatShows.city || "",
    description: boatShows.description || "",
    site: boatShows.site || "",
  });

  const countryRegions = getRegionNames(form.country);
  // Keep a saved region visible even if it isn't in the library's list
  const regions =
    form.region && !countryRegions.includes(form.region)
      ? [form.region, ...countryRegions]
      : countryRegions;

  const setField = (field) => (e) =>
    setForm({ ...form, [field]: e?.target ? e.target.value : e });

  const onCompleted = () => {
    toast("Boat Show Successfully Updated");
    closeEditForm();
  };

  const onError = (error) => {
    const message =
      error?.graphQLErrors?.map(({ message }) => message).join(", ") ||
      error?.message ||
      "Unknown error";
    toast.error(`Could not update boat show: ${message}`);
  };

  return (
    <Mutation
      mutation={UPDATE_BOAT_SHOWS}
      onCompleted={onCompleted}
      onError={onError}
    >
      {(updateBoatShow, { loading: saving }) => (
        <div className={classes.root}>
          <form
            autoComplete="off"
            onSubmit={(e) => {
              e.preventDefault();
              if (!dealer) {
                toast.error("Please select a dealer");
                return;
              }
              if (!form.country) {
                toast.error("Please select a country");
                return;
              }
              if (!form.region) {
                toast.error("Please select a state/region");
                return;
              }
              if (form.end_date && form.start_date > form.end_date) {
                toast.error("End Date must be on or after Start Date");
                return;
              }
              const profile = dealer.profile;
              updateBoatShow({
                variables: {
                  id: boatShows.id,
                  ...form,
                  dealer: dealer.label,
                  profile_id: profile?.profile_id || boatShows.profile_id,
                  brands: profile ? getBrands(profile) : boatShows.brands,
                  modified_at: "now()",
                },
              });
            }}
          >
            <Typography
              gutterBottom
              variant="h2"
              className={classes.heading}
            >
              Update Boat Show
            </Typography>
            <Grid container spacing={3} alignItems="flex-end">
              {/* Row 1: who and where it shows */}
              <Grid item xs={12} md={6}>
                <label className={classes.fieldLabel}>Dealer *</label>
                <Select
                  required
                  options={options}
                  isSearchable
                  isClearable
                  placeholder="Select a Dealer *"
                  onChange={setDealer}
                  value={dealer}
                  className={`dealerSelect ${classes.dealerSelect}`}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <label className={classes.fieldLabel}>Select Site *</label>
                <TextField
                  name="site"
                  id="site"
                  select
                  fullWidth
                  variant="outlined"
                  className={classes.siteSelect}
                  onChange={setField("site")}
                  value={form.site}
                  required
                >
                  <MenuItem value="CarolinaSkiff">Carolina Skiff</MenuItem>
                  <MenuItem value="SeaChaser">Sea Chaser</MenuItem>
                  <MenuItem value="CarolinaSkiff,SeaChaser">
                    CarolinaSkiff/ SeaChaser
                  </MenuItem>
                </TextField>
              </Grid>

              {/* Row 2: show name and dates */}
              <Grid item xs={12} md={6}>
                <TextField
                  {...fieldProps}
                  id="name"
                  label="Name of Show"
                  onChange={setField("name")}
                  value={form.name}
                  required
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  {...fieldProps}
                  id="start_date"
                  label="Start Date"
                  type="date"
                  onChange={setField("start_date")}
                  value={form.start_date}
                  required
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  {...fieldProps}
                  id="end_date"
                  label="End Date"
                  type="date"
                  onChange={setField("end_date")}
                  value={form.end_date}
                  required
                />
              </Grid>

              {/* Row 3: street address */}
              <Grid item xs={12} md={6}>
                <TextField
                  {...fieldProps}
                  id="address"
                  label="Address"
                  onChange={setField("address")}
                  value={form.address}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  {...fieldProps}
                  id="address2"
                  label="Suite, Apt # (optional)"
                  onChange={setField("address2")}
                  value={form.address2}
                />
              </Grid>

              {/* Row 4: city / state / zip / country */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  {...fieldProps}
                  id="city"
                  label="City"
                  onChange={setField("city")}
                  value={form.city}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  {...fieldProps}
                  id="country"
                  label="Country"
                  select
                  value={form.country}
                  onChange={(e) =>
                    // Changing country invalidates the selected state/region
                    setForm({ ...form, country: e.target.value, region: "" })
                  }
                  required
                >
                  {CountryRegionDataUSFirst.map((country) => (
                    <MenuItem key={country[1]} value={country[0]}>
                      {country[0]}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  {...fieldProps}
                  id="region"
                  label="State/Region"
                  select
                  value={form.region}
                  onChange={setField("region")}
                  required
                >
                  {regions.map((region) => (
                    <MenuItem key={region} value={region}>
                      {region}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  {...fieldProps}
                  id="zipcode"
                  label="Zipcode"
                  onChange={setField("zipcode")}
                  value={form.zipcode}
                  required
                />
              </Grid>

              {/* Row 5: description */}
              <Grid item xs={12}>
                <TextField
                  {...fieldProps}
                  id="description"
                  label="Boat Event Description"
                  helperText="Max 700 words"
                  onChange={setField("description")}
                  value={form.description}
                  required
                  multiline
                  rows="4"
                />
              </Grid>

              <Grid item xs={12} className={classes.actions}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={closeEditForm}
                  type="button"
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Update Boat Show"}
                </Button>
              </Grid>
            </Grid>
          </form>
        </div>
      )}
    </Mutation>
  );
};

const BoatShowsEdit = (props) => (
  // Always fetch fresh so dealers added this session are available
  <Query query={getAllDealerOptions} fetchPolicy="network-only">
    {({ data, loading, error }) => {
      if (loading) return <p>Loading...</p>;
      if (error) return <p>Error loading dealers</p>;
      return (
        <BoatShowsEditForm
          {...props}
          dealerOptions={getDealerOptions(data?.profiles)}
        />
      );
    }}
  </Query>
);

BoatShowsEdit.propTypes = {
  classes: PropTypes.object.isRequired,
  boatShows: PropTypes.object.isRequired,
  closeEditForm: PropTypes.func.isRequired,
};

export default withStyles(styles)(BoatShowsEdit);
